// Derived data layer — turns the flat mock fixtures into the agentic views
// the UI needs: a risk-ranked worklist, workflow-queue rollups, and lookups.
// Pure functions over the mock arrays; no network. Replace with API in Phase 2.

import {
  MOCK_PATIENTS, MOCK_PROVIDERS, MOCK_PAYERS, MOCK_PLANS, MOCK_APPOINTMENTS,
  MOCK_ELIGIBILITY_CHECKS, MOCK_WORKFLOW_ITEMS, MOCK_DISCREPANCIES, MOCK_CLAIMS,
  MOCK_LOCATIONS, MOCK_PRACTICES, MOCK_CONTRACTED_PLANS, CHARGE_BY_CPT,
} from '../mock';
import { addedPlansFor, planKeyFor, confidenceFor } from './networkStore';
import { priorAuthEnabled } from './features';
import { addedAppointmentsFor } from './apptStore';

// Workflow items visible given feature flags. Prior-auth/authorization is
// deferred for the pilot demo, so those items are hidden unless the flag is on.
const visibleMockWorkflowItems = () =>
  MOCK_WORKFLOW_ITEMS.filter((w) => priorAuthEnabled() || w.queue !== 'authorization');

// Tenant scope: 'all' aggregates across every practice; otherwise match one.
export const inScope = (practiceId, scope) => scope === 'all' || !scope || practiceId === scope;
export const practiceById = (id) => MOCK_PRACTICES.find((p) => p.id === id);
export const practiceName = (id) => (id === 'all' ? 'All practices' : practiceById(id)?.name || '');

/* ------------------------------- formatters ------------------------------- */

export const money = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const money2 = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const pct = (n) => (n == null ? '—' : `${Math.round(n * 100)}%`);

// Display DOB as MM/DD/YYYY (stored as YYYY-MM-DD). Avoids new Date() so there's
// no timezone shift on a date-only value.
export const fmtDob = (dob) => {
  if (!dob) return '—';
  const [y, m, d] = String(dob).split('-');
  return (y && m && d) ? `${m}/${d}/${y}` : dob;
};

export const shortDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Weekday + date, e.g. "Tue · Jun 24" — used in the worklist when the active
// timeframe spans multiple days so the date isn't ambiguous from the time alone.
export const dayLabel = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', ' ·');

export const time = (iso) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

export const dateTime = (iso) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function relTime(iso) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((now - then) / 60000); // minutes
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m ago`;
  const h = Math.round(diff / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function dueIn(iso) {
  const due = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.round((due - now) / 60000);
  if (mins < 0) return { text: `${Math.abs(Math.round(mins / 60))}h overdue`, overdue: true };
  if (mins < 60) return { text: `due in ${mins}m`, overdue: false, soon: true };
  const h = Math.round(mins / 60);
  if (h < 24) return { text: `due in ${h}h`, overdue: false, soon: h <= 4 };
  return { text: `due in ${Math.round(h / 24)}d`, overdue: false };
}

/* -------------------------------- lookups -------------------------------- */

export const patientById = (id) => MOCK_PATIENTS.find((p) => p.id === id);
export const providerById = (id) => MOCK_PROVIDERS.find((p) => p.id === id);
export const payerById = (id) => MOCK_PAYERS.find((p) => p.id === id);
export const planById = (id) => MOCK_PLANS.find((p) => p.id === id);
export const fullName = (p) => (p ? `${p.firstName} ${p.lastName}` : 'Unknown');
export const initials = (name) =>
  (name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

export const latestCheckForPatient = (patientId) =>
  MOCK_ELIGIBILITY_CHECKS.filter((c) => c.patientId === patientId)
    .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0] || null;

export const discrepanciesForPatient = (patientId) =>
  MOCK_DISCREPANCIES.filter((d) => d.patientId === patientId);

/* ----------------------------- risk scoring ------------------------------ */
// Returns { score 0-100, level: clear|low|medium|high|unverified, reasons[] }

/* ----------------------------- network status ---------------------------- */

// Resolve a check's returned plan to a network status against the practice's
// live network library. Precedence:
//   1) the practice's own additions (phone verification / CSV / manual) — so a
//      newly-verified plan flips status immediately, everywhere;
//   2) the contracted-plans DB;
//   3) whatever the check itself recorded (covers practices with no contracted
//      data, and is the source of the initial "not_in_db" detection).
// Returns { status, inDb, source }. status: in_network | out_of_network |
// unknown | not_in_db | unverified.
export function resolveNetwork(check, practiceId = check?.practiceId) {
  if (!check) return { status: 'unverified', inDb: false, source: null };
  const key = planKeyFor(check.payerId, check.planNameReturned);

  const added = addedPlansFor(practiceId).find((p) => p.planKey === key);
  if (added) return { status: added.status, inDb: true, source: added.source };

  const cp = MOCK_CONTRACTED_PLANS.find(
    (c) => inScope(c.practiceId, practiceId) && c.payerId === check.payerId &&
      planKeyFor(c.payerId, planById(c.planId)?.planName) === key
  );
  if (cp) return { status: cp.status, inDb: true, source: 'contracted' };

  const baked = check.networkStatusResolved;
  return { status: baked, inDb: baked !== 'not_in_db', source: '271' };
}

export const effectiveNetworkStatus = (check, practiceId) => resolveNetwork(check, practiceId).status;

// True when the returned plan can't be found in the practice's network library —
// the trigger for the network-verification workflow.
export const isUnknownPlan = (check, practiceId) => resolveNetwork(check, practiceId).status === 'not_in_db';

// Remaining-deductible cutoff that classifies a plan as "high deductible" (HDHP).
// One source of truth so the briefing, the worklist filter, and the summary
// bullets all agree on what counts as high-deductible.
export const HDHP_REMAINING = 3000;

const isChiropractic = (provider) => String(provider?.taxonomyCode || '').startsWith('111N');

// Turn a check into the de-cluttered, line-by-line read the front desk asked for:
// plan, network (verified by NPI), high-deductible, coinsurance, copay, and only
// the flags/notes that actually apply. Built from the structured fields, so it
// works for cached and live 271s alike. Returns [{ label, value, tone }].
export function summaryPoints(check, provider, payer) {
  if (!check) return [];
  const fin = check.financials || {};
  const cov = check.coverage || {};
  const flags = check.flags || {};
  const active = cov.active !== false;
  // Trust the network status the backend resolved on the 271 (contracted-plans DB
  // + network library), rather than recomputing from local mock data.
  const net = check.networkStatusResolved || 'unknown';
  const remaining = (fin.deductibleInd || 0) - (fin.deductibleIndMet || 0);
  const points = [];
  const add = (label, value, tone = 'neutral') => points.push({ label, value, tone });

  add('Plan', [payer?.name, check.planNameReturned].filter(Boolean).join(' · ') || '—');

  if (!active) {
    add('Coverage', 'Inactive — request updated insurance before the visit', 'rose');
    return points; // nothing else to collect on an inactive plan
  }

  if (net === 'in_network') add('Network', provider?.npi ? `In-network — verified by NPI ${provider.npi}` : 'In-network', 'green');
  else if (net === 'out_of_network') add('Network', 'Out-of-network for this practice', 'rose');
  else add('Network', 'Unknown — confirm with payer before the visit', 'amber');

  if (remaining > HDHP_REMAINING) add('High-deductible plan', `${money(remaining)} remaining`, 'amber');
  add('Coinsurance', pct(fin.coinsurance || 0), (fin.coinsurance || 0) > 0 ? 'amber' : 'green');
  if (fin.copay > 0) add('Copay', `${money(fin.copay)} at check-in`, 'amber');
  if (flags.referralRequired) add('Referral', 'Required from PCP', 'amber');
  if (flags.priorAuthRequired) add('Prior authorization', 'Required before the visit', 'rose');
  if (isChiropractic(provider)) add('Chiropractic', 'Visit caps often apply — verify annual limit', 'amber');

  return points;
}

export function scoreCheck(check, patient) {
  if (!check) return { score: null, level: 'unverified', reasons: ['Not yet verified'], factors: [] };

  const f = check.financials || {};
  const flags = check.flags || {};
  const factors = []; // { label, points, source } — drives the reasoning trace
  const add = (label, points, source) => factors.push({ label, points, source });

  if (check.coverage && check.coverage.active === false) add('Coverage inactive', 60, '271 · plan status');
  const net = resolveNetwork(check).status;
  if (net === 'out_of_network') add('Out-of-network plan', 35, 'contracted-plans DB');
  if (net === 'unknown') add('Network status unknown', 20, 'contracted-plans DB');
  if (net === 'not_in_db') add('Plan not in network database', 35, 'network library');
  if (flags.priorAuthRequired) add('Prior auth required', 18, '271 · auth indicator');
  if (flags.referralRequired) add('Referral required', 18, '271 · gatekeeper plan');

  const remaining = (f.deductibleInd || 0) - (f.deductibleIndMet || 0);
  if (check.coverage?.active !== false && remaining > 500) add(`High remaining deductible (${money(remaining)})`, 12, '271 · accumulators');
  if (patient?.isNewPatient) add('New patient', 8, 'practice record');

  const disc = discrepanciesForPatient(check.patientId).filter((d) => d.status === 'pending');
  if (disc.length) add(`${disc.length} data mismatch${disc.length > 1 ? 'es' : ''}`, 15, '271 vs PM');

  const score = Math.min(100, factors.reduce((s, x) => s + x.points, 0));
  let level = 'clear';
  if (score >= 55) level = 'high';
  else if (score >= 25) level = 'medium';
  else if (score > 0) level = 'low';

  const reasons = factors.length ? factors.map((x) => x.label) : ['In-network, no exceptions'];
  return { score, level, reasons, factors };
}

/* ------------------------------- worklist -------------------------------- */
// One row per appointment, joined + scored + linked to its workflow items.

export function buildWorklist(practiceId = 'prac_001') {
  const nv = networkVerificationItems(practiceId);
  const wfItems = visibleMockWorkflowItems();
  const appts = [
    ...MOCK_APPOINTMENTS.filter((a) => inScope(a.practiceId, practiceId)),
    ...addedAppointmentsFor(practiceId),
  ];
  return appts
    .map((apt) => {
      // Added appointments may carry an inline new-patient object; otherwise look up the roster.
      const patient = apt.patient || patientById(apt.patientId);
      const provider = providerById(apt.providerId);
      const payer = payerById(patient?.payerId);
      const plan = planById(patient?.planId);
      const check = latestCheckForPatient(apt.patientId);
      const risk = scoreCheck(check, patient);
      const workflows = [
        ...wfItems.filter((w) => w.appointmentId === apt.id && w.status === 'awaiting_review'),
        ...nv.filter((w) => w.appointmentId === apt.id),
      ];

      let agentState;
      if (!check) agentState = 'queued';
      else if (workflows.length) agentState = 'awaiting_review';
      else if (risk.level === 'clear' || risk.level === 'low') agentState = 'auto_cleared';
      else agentState = 'flagged';

      const wf = workflows[0];
      const ACTION = { communication: 'Send patient message', authorization: 'Initiate prior auth', referral: 'Confirm referral', data_sync: 'Apply data fix', network_verification: 'Verify network' };
      let actionLabel = null;
      if (agentState === 'awaiting_review' && wf) actionLabel = ACTION[wf.queue] || 'Review';
      else if (agentState === 'queued') actionLabel = 'Run eligibility';
      if (wf?.queue === 'communication' && /estimate|\$|out-of-network/i.test(wf.title + wf.recommendation)) actionLabel = 'Collect estimate';

      // For auto-cleared appointments, surface the concrete front-desk money
      // action so staff know what to do even though no review is needed
      // (Aravind: "a comment so practices know what to do with that appointment").
      let clearedAction = null;
      if (agentState === 'auto_cleared' && check) {
        const fin = check.financials || {};
        const copay = fin.copay || 0;
        const remaining = (fin.deductibleInd || 0) - (fin.deductibleIndMet || 0);
        if (copay > 0) clearedAction = { label: `Collect ${money(copay)} copay at check-in`, due: true };
        else if (check.coverage?.active !== false && remaining > 0) clearedAction = { label: 'Deductible not met — collect estimate before visit', due: true };
        else clearedAction = { label: 'Fully covered — nothing to collect', due: false };
      }

      return {
        id: apt.id, apt, patient, provider, payer, plan, check, risk, workflows, agentState, actionLabel, clearedAction,
        location: MOCK_LOCATIONS.find((l) => l.id === apt.locationId) || null,
        practice: practiceById(apt.practiceId),
        visitType: visitTypeFor(apt.cpt),
        recommended: wf?.recommendation || (clearedAction?.label) || (agentState === 'auto_cleared' ? 'No action needed — agent cleared.' : 'Review eligibility details.'),
      };
    })
    .sort((a, b) => (b.risk.score ?? -1) - (a.risk.score ?? -1) || new Date(a.apt.startTime) - new Date(b.apt.startTime));
}

export function worklistSummary(practiceId = 'prac_001') {
  const items = buildWorklist(practiceId);
  return {
    total: items.length,
    needsAttention: items.filter((i) => i.agentState === 'awaiting_review' || i.agentState === 'flagged').length,
    autoCleared: items.filter((i) => i.agentState === 'auto_cleared').length,
    queued: items.filter((i) => i.agentState === 'queued').length,
    high: items.filter((i) => i.risk.level === 'high').length,
    medium: items.filter((i) => i.risk.level === 'medium').length,
  };
}

/* ------------------------------- workflows ------------------------------- */

// Derived "network verification" queue — the calling department's worklist.
// An appointment lands here when its returned plan needs a phone confirmation:
//   • the plan isn't in the network library at all (not_in_db), OR
//   • it was only provisionally added (phone/CSV/manual) and hasn't yet reached
//     high confidence — so it keeps routing until the confirmation threshold.
// These items are computed live, so they auto-clear once a plan is verified to
// high confidence. The verification facts they produce live in the network store.
// Only phone confirmations are "provisional" (a single call is weak evidence and
// keeps routing until the threshold). CSV import and manual entry are explicit
// assertions by the practice — trusted immediately, never routed for calls.
const PROVISIONAL_SOURCES = new Set(['phone_call']);

export function networkVerificationItems(practiceId = 'prac_001') {
  return MOCK_APPOINTMENTS
    .filter((a) => inScope(a.practiceId, practiceId))
    .map((apt) => {
      const check = latestCheckForPatient(apt.patientId);
      if (!check) return null;
      const net = resolveNetwork(check, apt.practiceId);
      const conf = confidenceFor({
        planKey: planKeyFor(check.payerId, check.planNameReturned),
        practiceId: apt.practiceId,
        providerId: apt.providerId,
      });
      const verified = net.status !== 'not_in_db';
      const needsCall = net.status === 'not_in_db'
        || (PROVISIONAL_SOURCES.has(net.source) && conf.level === 'low');
      if (!needsCall) return null;

      const payer = payerById(check.payerId);
      const label = `${payer?.name || 'payer'} · ${check.planNameReturned}`;
      return {
        id: `nv_${apt.id}`,
        queue: 'network_verification',
        practiceId: apt.practiceId,
        patientId: apt.patientId,
        appointmentId: apt.id,
        providerId: apt.providerId,
        payerId: check.payerId,
        planId: null,
        planNameReturned: check.planNameReturned,
        payerName: payer?.name,
        cpt: apt.cpt,
        priority: verified ? 'medium' : 'high',
        status: 'awaiting_review',
        confidence: 0.92,
        netConfidence: conf,
        createdAt: check.submittedAt,
        dueAt: apt.startTime,
        slaHours: 24,
        title: verified
          ? `Confirm network — ${label} (${conf.count}/${conf.threshold})`
          : `Unknown plan — verify ${label}`,
        agentSummary: verified
          ? `Plan was confirmed in-network by phone but is still building confidence (${conf.count} of ${conf.threshold} confirmations across distinct patients). Routing for another confirmation before it auto-clears the calling queue.`
          : `The 271 returned "${check.planNameReturned}" under ${payer?.name}, which isn't in the practice network library — status can't be resolved from the contracted-plans DB. Call the payer to confirm in/out-of-network before the visit.`,
        recommendation: verified
          ? 'Confirm network status by phone to add another verification toward the threshold.'
          : 'Call the payer to verify network status, then capture the confirmation (rep name + reference number).',
        draft: null,
      };
    })
    .filter(Boolean);
}

// All workflow items in scope: the static fixtures plus the live network queue.
export function allWorkflowItems(practiceId = 'prac_001') {
  return [
    ...visibleMockWorkflowItems().filter((w) => inScope(w.practiceId, practiceId)),
    ...networkVerificationItems(practiceId),
  ];
}

export function workflowItems(practiceId = 'prac_001', queue = null) {
  return allWorkflowItems(practiceId)
    .filter((w) => !queue || w.queue === queue)
    .sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt));
}

export function queueCounts(practiceId = 'prac_001') {
  const open = allWorkflowItems(practiceId).filter((w) => w.status === 'awaiting_review');
  return open.reduce((acc, w) => ((acc[w.queue] = (acc[w.queue] || 0) + 1), acc), {});
}

export function openWorkflowCount(practiceId = 'prac_001') {
  return allWorkflowItems(practiceId).filter((w) => w.status === 'awaiting_review').length;
}

/* --------------------------------- claims -------------------------------- */

export function claimsStats(practiceId = 'prac_001') {
  const claims = MOCK_CLAIMS.filter((c) => inScope(c.practiceId, practiceId));
  const denied = claims.filter((c) => c.status === 'denied');
  const charged = claims.reduce((s, c) => s + (c.totalCharge || 0), 0);
  const paid = claims.reduce((s, c) => s + (c.totalPaid || 0), 0);
  return {
    total: claims.length,
    denied: denied.length,
    denialRate: claims.length ? denied.length / claims.length : 0,
    charged, paid,
  };
}

/* -------------------- Stedi 271 analytics (PHI-excluded) ------------------ */
// Everything derivable from eligibility responses in aggregate, over a period —
// network mix, coverage status, plan/cost-share landscape, payer behavior,
// referral/auth exposure, automation rate, and data quality. No PHI.
export function stediAnalytics(practiceId = 'prac_001') {
  const checks = MOCK_ELIGIBILITY_CHECKS.filter((c) => inScope(c.practiceId, practiceId));
  const n = checks.length || 1;
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

  const active = checks.filter((c) => c.coverage?.active !== false).length;
  const net = { in: 0, out: 0, unknown: 0 };
  checks.forEach((c) => { net[c.networkStatusResolved === 'in_network' ? 'in' : c.networkStatusResolved === 'out_of_network' ? 'out' : 'unknown'] += 1; });

  const planTypes = {};
  checks.forEach((c) => { const t = c.coverage?.planType || 'Other'; planTypes[t] = (planTypes[t] || 0) + 1; });

  const referral = checks.filter((c) => c.flags?.referralRequired).length;
  const priorAuth = checks.filter((c) => c.flags?.priorAuthRequired).length;
  const levels = checks.map((c) => scoreCheck(c, patientById(c.patientId)).level);
  const autoClear = levels.filter((l) => l === 'clear' || l === 'low').length;

  const pm = {};
  checks.forEach((c) => {
    const p = payerById(c.payerId)?.name || 'Unknown';
    (pm[p] ||= { count: 0, oon: 0, ref: 0, auth: 0, copaySum: 0, inactive: 0 });
    const m = pm[p];
    m.count += 1;
    if (c.networkStatusResolved === 'out_of_network') m.oon += 1;
    if (c.flags?.referralRequired) m.ref += 1;
    if (c.flags?.priorAuthRequired) m.auth += 1;
    if (c.coverage?.active === false) m.inactive += 1;
    m.copaySum += c.financials?.copay || 0;
  });
  const byPayer = Object.entries(pm).map(([name, m]) => ({
    name, count: m.count, oonRate: m.oon / m.count, refRate: m.ref / m.count,
    authRate: m.auth / m.count, avgCopay: m.copaySum / m.count, inactive: m.inactive,
  })).sort((a, b) => b.count - a.count);

  const mismatchCount = MOCK_DISCREPANCIES.filter((d) => d.status !== 'dismissed').length;
  const autoPct = Math.round((autoClear / n) * 100);
  const autoClearTrend = [58, 64, 69, 73, 76, autoPct]; // weekly, illustrative tail = current

  return {
    total: checks.length,
    activeRate: active / n,
    inactiveRate: 1 - active / n,
    net, netPct: { in: net.in / n, out: net.out / n, unknown: net.unknown / n },
    planTypes,
    referralRate: referral / n,
    priorAuthRate: priorAuth / n,
    autoClearRate: autoClear / n,
    avgDeductible: avg(checks.map((c) => c.financials?.deductibleInd || 0)),
    avgCopay: avg(checks.filter((c) => c.coverage?.active !== false).map((c) => c.financials?.copay || 0)),
    avgCoinsurance: avg(checks.map((c) => c.financials?.coinsurance || 0)),
    byPayer, mismatchCount, autoClearTrend,
  };
}

// Weekly time-series for the analytics charts (illustrative, deterministic).
export function stediTrends(practiceId = 'prac_001', weeks = 12) {
  const a = stediAnalytics(practiceId);
  const target = a.autoClearRate || 0.8;
  const out = [];
  let auto = 0.64;
  for (let i = 0; i < weeks; i += 1) {
    const checks = 178 + Math.round(Math.sin(i / 2) * 16) + i * 3;
    auto = Math.min(target + 0.06, auto + 0.012);
    const cleared = Math.round(checks * auto);
    out.push({
      week: `W${i + 1}`,
      checks,
      cleared,
      attention: checks - cleared,
      autoPct: Math.round(auto * 100),
      oonPct: Math.max(6, Math.round((18 - i * 0.6) )),
      collected: 6.2 + i * 0.42 + Math.sin(i / 3), // $k flagged to collect
    });
  }
  return out;
}

/* ------------------------------ misc helpers ----------------------------- */

export const RISK_LEVELS = {
  high: { label: 'High', cls: 'pill-red', dot: 'bg-rose-500' },
  medium: { label: 'Medium', cls: 'pill-amber', dot: 'bg-amber-500' },
  low: { label: 'Low', cls: 'pill-sky', dot: 'bg-sky-500' },
  clear: { label: 'Clear', cls: 'pill-green', dot: 'bg-emerald-500' },
  unverified: { label: 'Unverified', cls: 'pill-slate', dot: 'bg-slate-400' },
};

/* -------------------------- timeframe + locations ------------------------- */
// Demo "today" is the real local date — the seed shifts appointments onto it so
// the Today / Tomorrow filters populate against whatever day the demo runs.
const _localDate = (d = new Date()) => {
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};
export const NOW_DATE = _localDate();
const DAY = 86400000;

export const TIMEFRAMES = [
  { k: 'today', label: 'Today' },
  { k: 'tomorrow', label: 'Tomorrow' },
  { k: 'next30', label: 'Next 30 days' },
  { k: 'past30', label: 'Past 30 days' },
  { k: 'all', label: 'All' },
];

export function inTimeframe(startTime, tf) {
  if (!tf || tf === 'all') return true;
  const d = startTime.slice(0, 10);
  const t = new Date(`${d}T00:00:00Z`).getTime();
  const now = new Date(`${NOW_DATE}T00:00:00Z`).getTime();
  if (tf === 'today') return d === NOW_DATE;
  if (tf === 'tomorrow') return t === now + DAY;
  if (tf === 'next30') return t > now && t <= now + 30 * DAY;
  if (tf === 'past30') return t < now && t >= now - 30 * DAY;
  return true;
}

export const locationById = (id) => MOCK_LOCATIONS.find((l) => l.id === id);
export const locationsFor = (practiceId = 'prac_001') => MOCK_LOCATIONS.filter((l) => inScope(l.practiceId, practiceId));

/* ----------------------------- visit / finance --------------------------- */
export function visitTypeFor(cpt) {
  if (['17311', '17312'].includes(cpt)) return 'Surgery';
  if (['17110', '11102', '11042'].includes(cpt)) return 'Procedure';
  if (['99203', '99204', '99205'].includes(cpt)) return 'New patient';
  return 'Follow-up';
}

// CFO view — practice-wide rollup of the loaded appointments by type and payer.
export function financeOverview(practiceId = 'prac_001') {
  const wl = buildWorklist(practiceId);
  const rows = wl.map((w) => ({ type: w.visitType, payer: w.payer?.name || 'Unknown', charge: CHARGE_BY_CPT[w.apt.cpt] || 150 }));
  const group = (key) => {
    const m = {};
    rows.forEach((r) => { (m[r[key]] ||= { count: 0, revenue: 0 }); m[r[key]].count += 1; m[r[key]].revenue += r.charge; });
    return Object.entries(m).map(([k, v]) => ({ key: k, ...v })).sort((a, b) => b.revenue - a.revenue);
  };
  const projected = rows.reduce((s, r) => s + r.charge, 0);
  // patient responsibility "at stake" — OON + high-deductible + denied
  const atStake = wl.filter((w) => w.risk.level === 'high').reduce((s, w) => s + (CHARGE_BY_CPT[w.apt.cpt] || 150) * 0.5, 0)
    + MOCK_CLAIMS.filter((c) => inScope(c.practiceId, practiceId) && c.status === 'denied').reduce((s, c) => s + c.totalCharge, 0);
  return { byType: group('type'), byPayer: group('payer'), projected, count: rows.length, atStake };
}

// Roll up an already-filtered set of worklist items (for the CFO view filters).
export function financeRollup(items) {
  const rows = items.map((w) => ({ type: w.visitType, payer: w.payer?.name || 'Unknown', practice: w.practice?.name || '—', charge: CHARGE_BY_CPT[w.apt.cpt] || 150, high: w.risk.level === 'high' }));
  const group = (key) => {
    const m = {};
    rows.forEach((r) => { (m[r[key]] ||= { count: 0, revenue: 0 }); m[r[key]].count += 1; m[r[key]].revenue += r.charge; });
    return Object.entries(m).map(([k, v]) => ({ key: k, ...v })).sort((a, b) => b.revenue - a.revenue);
  };
  return {
    byType: group('type'), byPayer: group('payer'), byPractice: group('practice'),
    projected: rows.reduce((s, r) => s + r.charge, 0),
    atStake: rows.filter((r) => r.high).reduce((s, r) => s + r.charge * 0.5, 0),
    count: rows.length,
  };
}
